// Conversation/index.tsx
import { Dropdown, Input, Menu } from 'antd';
import classNames from 'classnames';
import {
  useEffect,
  useState,
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  memo,
} from 'react';
import ConversationModal from '../components/ConversationModal';
import { deleteConversation, saveConversation } from '../service'; // 移除 getAllConversations 导入
import styles from './style.module.less';
import { AgentType, ConversationDetailType } from '../type';
import { DEFAULT_CONVERSATION_NAME } from '../constants';
import moment from 'moment';
import { CloseOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';

type Props = {
  agentId?:string;
  skillId?: string;
  currentAgent?: AgentType;
  currentConversation?: ConversationDetailType;
  historyVisible?: boolean;
  onSelectConversation: (
    conversation: ConversationDetailType,
    sendMsgParams?: any,
    isAdd?: boolean
  ) => void;
  onCloseConversation: () => void;
};

const Conversation: ForwardRefRenderFunction<any, Props> = (
  { currentAgent, currentConversation, historyVisible, onSelectConversation, onCloseConversation, agentId,
  skillId, },
  ref
) => {
  const [conversations, setConversations] = useState<ConversationDetailType[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editConversation, setEditConversation] = useState<ConversationDetailType>();
  const [searchValue, setSearchValue] = useState('');

  useImperativeHandle(ref, () => ({
    updateData: () => Promise.resolve([]), // 返回空数组，避免调用接口
    onAddConversation,
  }));

  // 修改 updateData 函数，不再调用接口
  const updateData = async (agentId?: number) => {
    // 不再调用 getAllConversations 接口
    // 可以返回空数组或使用传入的数据
    return [];
  };

  const initData = async () => {
    // 不再调用 getAllConversations 接口
    onAddConversation();
  };

  useEffect(() => {
    if (currentAgent && conversations.length === 0) {
      if (currentAgent.initialSendMsgParams) {
        onAddConversation(currentAgent.initialSendMsgParams);
      } else {
        onAddConversation();
      }
    } else if (currentAgent && !currentConversation) {
      // 不调用 updateData()
    }
  }, [currentAgent, currentConversation]);

  const addConversation = async (sendMsgParams?: any) => {
    // 不再调用 getAllConversations 接口
    return [];
  };
  
  const onDeleteConversation = async (id: number) => {
    await deleteConversation(id);
    // 不再调用 initData()，可以手动更新状态
    setConversations(prev => prev.filter(conv => conv.chatId !== id));
  };
  
  const onAddConversation = async (sendMsgParams?: any) => {
    // 不再调用 addConversation，直接触发 onSelectConversation
    // 这里可以根据需要创建一个临时的对话对象
    const newConversation: ConversationDetailType = {
      chatId: -1, // 临时ID
      chatName: DEFAULT_CONVERSATION_NAME,
    };
    onSelectConversation(newConversation, sendMsgParams, true);
  };
  
  const onOperate = (key: string, conversation: ConversationDetailType) => {
    if (key === 'editName') {
      setEditConversation(conversation);
      setEditModalVisible(true);
    } else if (key === 'delete') {
      onDeleteConversation(conversation.chatId);
    }
  };

  const conversationClass = classNames(styles.conversation, {
    [styles.historyVisible]: historyVisible,
  });

  const convertTime = (date: string) => {
    moment.locale('zh-cn');
    const now = moment();
    const inputDate = moment(date);
    const diffMinutes = now.diff(inputDate, 'minutes');
    if (diffMinutes < 1) {
      return '刚刚';
    } else if (inputDate.isSame(now, 'day')) {
      return inputDate.format('HH:mm');
    } else if (inputDate.isSame(now.subtract(1, 'day'), 'day')) {
      return '昨天';
    }
    return inputDate.format('MM/DD');
  };

  const onSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  return (
    <div className={conversationClass}>
      <div className={styles.rightSection}>
        <div className={styles.titleBar}>
          <div className={styles.title}>历史对话</div>
          <div className={styles.rightOperation}>
            <div
              className={styles.newConversation}
              onClick={() => {
                onAddConversation();
              }}
            >
              新对话
            </div>
            <CloseOutlined className={styles.closeIcon} onClick={onCloseConversation} />
          </div>
        </div>
        <div className={styles.searchConversation}>
          <Input
            placeholder="搜索"
            prefix={<SearchOutlined className={styles.searchIcon} />}
            className={styles.searchTask}
            value={searchValue}
            onChange={onSearchValueChange}
            allowClear
          />
        </div>
        <div className={styles.conversationList}>
          {conversations
            .filter(
              conversation =>
                searchValue === '' ||
                conversation.chatName.toLowerCase().includes(searchValue.toLowerCase())
            )
            .map(item => {
              const conversationItemClass = classNames(styles.conversationItem, {
                [styles.activeConversationItem]: currentConversation?.chatId === item.chatId,
              });
              return (
                <Dropdown
                  key={item.chatId}
                  overlay={
                    <Menu
                      items={[
                        { label: '修改对话名称', key: 'editName' },
                        { label: '删除', key: 'delete' },
                      ]}
                      onClick={({ key }) => {
                        onOperate(key, item);
                      }}
                    />
                  }
                  trigger={['contextMenu']}
                >
                  <div
                    className={conversationItemClass}
                    onClick={() => {
                      onSelectConversation(item);
                    }}
                  >
                    <div className={styles.conversationContent}>
                      <div className={styles.topTitleBar}>
                        <div className={styles.conversationTitleBar}>
                          <div className={styles.conversationName}>{item.chatName}</div>
                          {currentConversation?.chatId === item.chatId && (
                            <div className={styles.currentConversation}>当前对话</div>
                          )}
                        </div>
                        <div className={styles.conversationTime}>
                          {convertTime(item.lastTime || '')}
                        </div>
                      </div>
                      <div className={styles.bottomSection}>
                        <div className={styles.subTitle}>{item.lastQuestion}</div>
                        <DeleteOutlined
                          className={styles.deleteIcon}
                          onClick={e => {
                            e.stopPropagation();
                            onDeleteConversation(item.chatId);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Dropdown>
              );
            })}
        </div>
      </div>
      <ConversationModal
        visible={editModalVisible}
        editConversation={editConversation}
        onClose={() => {
          setEditModalVisible(false);
        }}
        onFinish={() => {
          setEditModalVisible(false);
          // 不再调用 updateData()
        }}
      />
    </div>
  );
};

function areEqual(prevProps: Props, nextProps: Props) {
  if (
    prevProps.currentAgent?.id === nextProps.currentAgent?.id &&
    prevProps.currentConversation?.chatId === nextProps.currentConversation?.chatId &&
    prevProps.historyVisible === nextProps.historyVisible
  ) {
    return true;
  }
  return false;
}

export default memo(forwardRef(Conversation), areEqual);